/**
 * A composable for handling direct modal triggers via URL parameters.
 * This utility allows modals to be triggered by specific URL parameters,
 * providing a way to directly open modals through URL navigation.
 *
 * @module useDirectModals
 *
 * @example
 * // Usage in a component
 * const { detect } = useDirectModals();
 * detect(); // Will check URL parameters and trigger modal if match found
 *
 * // URL examples that would trigger modals:
 * // domain.com?login=true
 * // domain.com?signup=1
 * // domain.com?login=open
 */

export const useDirectModals = () => {
    /**
     * Configuration object for available modals.
     * Each key represents a URL parameter that can trigger the modal.
     * @type {Object.<string, {name: string, params: Object}>}
     */
    const modals = {
        login: { name: "modal-authentication", params: { tab: "login" } },
        signup: { name: "modal-authentication", params: { tab: "signup" } },
    };

    /**
     * List of URL parameter values that are considered valid triggers.
     * @type {string[]}
     */
    const allowedValues = ["true", "1", "open"];

    /**
     * Array of valid modal keys that can be triggered.
     * @type {string[]}
     */
    const allowedModalKeys = Object.keys(modals);

    /**
     * URLSearchParams instance for current URL.
     * @type {URLSearchParams}
     */
    const urlParams = new URLSearchParams(window.location.search);

    /**
     * Array of current URL parameter keys.
     * @type {string[]}
     */
    const urlVars = Array.from(urlParams.keys());

    /**
     * Validates if a URL parameter value is allowed to trigger a modal.
     * @private
     * @param {string} value - The value to validate
     * @returns {boolean} True if the value is valid
     */
    const _isValid = (value) => {
        return allowedValues.includes(value);
    };

    /**
     * Detects if any modal should be triggered based on URL parameters.
     * If a valid modal trigger is found in the URL, dispatches a Livewire
     * event to show the corresponding modal.
     *
     * @public
     * @returns {void}
     *
     * @example
     * // With URL: domain.com?login=true
     * detect(); // Will dispatch 'modal-authentication' event with login params
     */
    const detect = () => {
        const modal = allowedModalKeys.filter((key) => urlVars.includes(key));
        if (modal.length > 0 && _isValid(urlParams.get(modal[0]))) {
            Livewire.dispatch(modals[modal[0]].name, modals[modal[0]].params);
        }
    };

    return { detect };
};
